import { ContractSummary } from "@/api/ContractSummary";
import { Flex, SimpleGrid } from "@chakra-ui/react";
import { deLocaleOption, formatDay, formatMinutes } from "@/data/DateTimeUtil";
import { format } from "date-fns";
import { NamingUtil } from "@/data/NamingUtil";
import { LuBriefcaseBusiness, LuCalendarCheck, LuCalendarDays, LuClock, LuTimer, LuTreePalm } from "react-icons/lu";
import type { IconType } from "react-icons";
import type { ReactNode } from "react";
import StatCard from "@/components/StatCard";
import { ApprovalSummary, hasPendingApprovals } from "@/data/ApprovalSummaryUtil";

interface ContractTabsProps {
    summary: ContractSummary | null;
    monthMinutes: number | null;
    month: Date;
    approvalSummary?: Partial<ApprovalSummary> | null;
    approvalScopeLabel?: string;
    showContractStats?: boolean;
    [key: string]: any;
}

type ContractStat = {
    key: string;
    label: string;
    value: string;
    unit?: string;
    helpText: ReactNode;
    icon: IconType;
    accentColor: string;
    iconBg: string;
    valueColor?: string;
};

const ContractTabs = ({
    summary,
    monthMinutes,
    month,
    approvalSummary = null,
    approvalScopeLabel,
    showContractStats = true,
    ...props
}: ContractTabsProps) => {
    const availableDays = summary?.absenceDays ?? null;
    const overTime = summary ? summary.workedMinutes - summary.requiredMinutes : null;
    const isUnderTime = overTime !== null && overTime < 0;
    const isOverTime = overTime !== null && overTime > 0;
    const extraTime = overTime === null ? null : Math.abs(overTime);
    const monthLabel = format(month, 'MMMM', deLocaleOption);
    const overtimeHelp = overTime === null
        ? 'n.a.'
        : isUnderTime
            ? 'Unterstunden'
            : isOverTime
                ? 'Überstunden'
                : 'Ausgeglichen';
    const pendingApprovals: ApprovalSummary = {
        timesheetMinutes: approvalSummary?.timesheetMinutes ?? 0,
        absenceDays: approvalSummary?.absenceDays ?? 0,
    };
    const scopeLabel = approvalScopeLabel ?? monthLabel;

    const approvalStats: ContractStat[] = hasPendingApprovals(pendingApprovals)
        ? [
            ...(pendingApprovals.timesheetMinutes > 0 ? [{
                key: 'pendingTimesheets',
                label: 'Offene Stunden',
                value: formatMinutes(pendingApprovals.timesheetMinutes),
                unit: 'h',
                helpText: `${scopeLabel} nicht genehmigt`,
                icon: LuTimer,
                accentColor: 'yellow.600',
                iconBg: 'yellow.50',
                valueColor: 'yellow.fg',
            }] : []),
            ...(pendingApprovals.absenceDays > 0 ? [{
                key: 'pendingAbsences',
                label: 'Offene Absenzen',
                value: formatDay(pendingApprovals.absenceDays),
                unit: 'Tage',
                helpText: `${scopeLabel} nicht genehmigt`,
                icon: LuCalendarCheck,
                accentColor: 'purple.600',
                iconBg: 'purple.50',
                valueColor: 'purple.fg',
            }] : []),
        ]
        : [];

    const contractStats: ContractStat[] = showContractStats ? [
        {
            key: 'balance',
            label: 'Erfasste Stunden',
            value: monthMinutes == null ? 'n.v.' : formatMinutes(monthMinutes),
            unit: monthMinutes == null ? undefined : 'h',
            helpText: <>Erfasst · {format(month, 'MMMM yyyy', deLocaleOption)}<br />Inkl. offener Einträge</>,
            icon: LuCalendarDays,
            accentColor: 'blue.600',
            iconBg: 'blue.50',
        },
        {
            key: 'overtime',
            label: 'Überstunden',
            value: extraTime === null ? 'n.v.' : formatMinutes(extraTime),
            unit: extraTime === null ? undefined : 'h',
            helpText: summary ? <>{overtimeHelp}<br />Inkl. offener Einträge</> : overtimeHelp,
            icon: LuClock,
            accentColor: isUnderTime ? 'red.600' : isOverTime ? 'green.600' : 'gray.600',
            iconBg: isUnderTime ? 'red.50' : isOverTime ? 'green.50' : 'gray.100',
            valueColor: isUnderTime ? 'red.600' : isOverTime ? 'green.600' : undefined,
        },
        {
            key: 'vacation',
            label: 'Feriensaldo',
            value: availableDays === null ? 'n.v.' : formatDay(availableDays),
            helpText: 'Abzgl. offener und genehmigter Ferien',
            icon: LuTreePalm,
            accentColor: 'teal.600',
            iconBg: 'teal.50',
        },
        {
            key: 'contract',
            label: 'Arbeitszeitmodell',
            value: summary ? NamingUtil.minutesPerWeek(summary.contract) : 'n.v.',
            unit: summary ? 'h' : undefined,
            helpText: summary ? NamingUtil.contract(summary.contract) : 'Einstellen',
            icon: LuBriefcaseBusiness,
            accentColor: 'orange.600',
            iconBg: 'orange.50',
        },
    ] : [];

    const stats: ContractStat[] = [...approvalStats, ...contractStats];
    const columnCount = Math.max(1, stats.length);

    return (
        <Flex justify='start' {...props}>
            <SimpleGrid columns={{ base: 1, md: columnCount }} gap='4'>
                {stats.map((stat) => (
                    <StatCard
                        key={stat.key}
                        label={stat.label}
                        value={stat.value}
                        unit={stat.unit}
                        helpText={stat.helpText}
                        icon={stat.icon}
                        accentColor={stat.accentColor}
                        iconBg={stat.iconBg}
                        valueColor={stat.valueColor}
                    />
                ))}
            </SimpleGrid>
        </Flex>
    )
};

export default ContractTabs;
